import { useState, useEffect } from "react";
import styles from "../../pages/Community/CreateCommunityPage.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import dayjs from "dayjs";
import CustomDatePicker from "../Calendar/CustomDatePicker";

// ─── PER-MEMBERSHIP EXPIRY PICKER ────────────────────────────────────────────
// Mirrors the Flutter per-plan expiry picker in VideoManagerWidget
const MembershipExpiryPicker = ({
  membershipId,
  label,
  expiryMap, // { [membership_id]: isoString | null }
  onSet, // (membershipId, isoString) => void
  onClear, // (membershipId) => void
  isSubmitting,
}) => {
  const existing = expiryMap?.[membershipId] ?? null;

  // Local UI state for date/time picker flow
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickedDate, setPickedDate] = useState(null); // dayjs
  const [pickedTime, setPickedTime] = useState("");

  // Sync from existing value when editing
  useEffect(() => {
    if (existing) {
      const dt = dayjs(existing);
      if (dt.isValid()) {
        setPickedDate(dt);
        setPickedTime(dt.format("HH:mm"));
        return;
      }
    }
    setPickedDate(null);
    setPickedTime("");
  }, [existing]);

  // Whenever date+time both set → push ISO to parent
  useEffect(() => {
    if (!pickedDate || !pickedTime) return;
    const dateStr = dayjs(pickedDate).format("YYYY-MM-DD");
    const iso = dayjs(`${dateStr} ${pickedTime}:00`).toISOString();
    onSet(membershipId, iso);
  }, [pickedDate, pickedTime]);

  const handleClear = () => {
    setPickedDate(null);
    setPickedTime("");
    setShowDatePicker(false);
    onClear(membershipId);
  };

  const displayValue = existing
    ? dayjs(existing).format("DD MMM YYYY HH:mm")
    : "No expiry (lifetime)";

  return (
    <div className={styles.sectionsCard} style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <label className={styles.inlineLabel}>{label}</label>
        {existing && (
          <button
            type="button"
            className={styles.deleteIconButton}
            onClick={handleClear}
            disabled={isSubmitting}
            title="Clear expiry (lifetime)"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Current value display */}
      <div
        style={{
          padding: "8px 12px",
          background: existing ? "#fff3cd" : "#e8f5e9",
          borderRadius: 6,
          marginBottom: 8,
          fontSize: 13,
          color: existing ? "#856404" : "#2e7d32",
          cursor: "pointer",
        }}
        onClick={() => setShowDatePicker((v) => !v)}
      >
        📅 {displayValue} {showDatePicker ? "▲" : "▼"}
      </div>

      {/* Date picker */}
      {showDatePicker && (
        <div>
          <CustomDatePicker
            preSelectedDate={pickedDate}
            multiSelect={false}
            disabled={isSubmitting}
            onDateSelect={(date) => {
              setPickedDate(date);
              setPickedTime(""); // reset time when date changes
            }}
          />

          {/* Time picker — shown after date is selected */}
          {pickedDate && (
            <div className={styles.rowInputGroup} style={{ marginTop: 8 }}>
              <label className={styles.inlineLabel}>Expiry Time</label>
              <input
                type="time"
                value={pickedTime}
                disabled={isSubmitting}
                className={styles.textInput}
                onChange={(e) => setPickedTime(e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── MAIN VIDEO FORM ──────────────────────────────────────────────────────────
const VideoForm = ({
  currentVideo,
  setCurrentVideo,
  editVideoIndex,
  isVideoFree,
  setIsVideoFree,
  isSubmitting,
  handleVideoCoverUpload,
  handleAddOrUpdateVideo,
  handleMembershipChange,
  setMembershipExpiry, // new
  clearMembershipExpiry, // new
}) => {
  const [memberships, setMemberships] = useState([]);
  const [searchTextMembership, setSearchTextMembership] = useState("");

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        if (data.success) {
          setMemberships(data.memberships);
        }
      } catch (err) {
        console.error("Failed to fetch memberships", err);
      }
    };
    fetchMemberships();
  }, []);

  // Helper: get membership label by id
  const getMembershipLabel = (id) => {
    const found = memberships.find((m) => Number(m.id) === Number(id));
    return found?.label || found?.name || `Plan #${id}`;
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCurrentVideo((prev) => ({ ...prev, attachment_file: file }));
  };

  const removeAttachment = () => {
    setCurrentVideo((prev) => ({
      ...prev,
      attachment_file: null,
      attachment_file_url: null,
    }));
  };

  const filteredMemberships = memberships.filter((membership) =>
    membership.label.toLowerCase().includes(searchTextMembership.toLowerCase()),
  );

  const expiryMap = currentVideo.membership_expiry || {};

  return (
    <>
      {/* ── Video Cover Upload ── */}
      <div className={styles.imageSectionWrapper}>
        <label className={styles.inlineLabel}>Video Cover Image</label>
        <div className={styles.featuredImageBox}>
          {currentVideo.cover_image ? (
            <>
              <img
                src={currentVideo.cover_image}
                alt="Video Cover"
                className={styles.featuredImageBox}
              />
              <button
                type="button"
                onClick={() =>
                  setCurrentVideo({
                    ...currentVideo,
                    cover_image: null,
                    cover_image_file: null,
                    delete_cover_image: true,
                  })
                }
                className={styles.removeImageButton}
                disabled={isSubmitting}
                title="Remove image"
              >
                ✕
              </button>
            </>
          ) : (
            <div className={styles.iconPlaceholderContainer}>
              <img
                src={placeholderImage}
                alt="Placeholder"
                className={styles.iconPlaceholder}
              />
              <div className={styles.uploadLinks}>
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Video Cover
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleVideoCoverUpload}
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Video Title ── */}
      <input
        type="text"
        placeholder="Video title"
        value={currentVideo.title ?? ""}
        onChange={(e) =>
          setCurrentVideo({ ...currentVideo, title: e.target.value })
        }
        className={styles.textInput}
        disabled={isSubmitting}
      />

      {/* ── Video URL ── */}
      <input
        type="text"
        placeholder="Video URL (YouTube or Vimeo)"
        value={currentVideo.url}
        onChange={(e) =>
          setCurrentVideo({ ...currentVideo, url: e.target.value })
        }
        className={styles.textInput}
        disabled={isSubmitting}
      />

      {/* ── Free / Paid Toggle ── */}
      <div className={styles.liveSessionRow}>
        <div className={styles.toggleWrapper}>
          <button
            className={`${styles.toggleOption} ${isVideoFree ? styles.activeToggle : ""}`}
            onClick={() => {
              setIsVideoFree(true);
              // Clear paid membership expiries, keep free (id=0) expiry
              setCurrentVideo((prev) => ({
                ...prev,
                membership_ids: [],
                membership_expiry: { 0: prev.membership_expiry?.[0] ?? null },
              }));
            }}
            disabled={isSubmitting}
          >
            Free
          </button>
          <button
            className={`${styles.toggleOption} ${!isVideoFree ? styles.activeToggle : ""}`}
            onClick={() => {
              setIsVideoFree(false);
              // Clear free expiry (id=0) when switching to paid
              setCurrentVideo((prev) => {
                const updated = { ...prev.membership_expiry };
                delete updated[0];
                return { ...prev, membership_expiry: updated };
              });
            }}
            disabled={isSubmitting}
          >
            Paid
          </button>
        </div>
      </div>

      {/* ── Membership selector (paid only) ── */}
      {!isVideoFree && (
        <div className={styles.sectionsCard}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="Filter memberships..."
              className={styles.searchInput}
              value={searchTextMembership}
              onChange={(e) => setSearchTextMembership(e.target.value)}
              disabled={isSubmitting}
            />
            <span className={styles.searchIcon}>🔍</span>
          </div>

          <div className={styles.sectionList}>
            {filteredMemberships.map((membership, i) => (
              <div className={styles.sectionRow} key={i}>
                <span className={styles.sectionLabel}>{membership.label}</span>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={(currentVideo.membership_ids || []).some(
                    (id) => Number(id) === Number(membership.id),
                  )}
                  disabled={isSubmitting}
                  onChange={() => handleMembershipChange(membership)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Per-Membership Expiry — mirrors Flutter ── */}
      <div style={{ marginTop: 12 }}>
        <label className={styles.inlineLabel}>
          {isVideoFree ? "Free Video Expiry" : "Membership Expiry Dates"}
        </label>

        {isVideoFree ? (
          // Free video: single picker, membership_id = 0
          <MembershipExpiryPicker
            membershipId={0}
            label="Free Video Expiry"
            expiryMap={expiryMap}
            onSet={setMembershipExpiry}
            onClear={clearMembershipExpiry}
            isSubmitting={isSubmitting}
          />
        ) : // Paid video: one picker per selected membership plan
        (currentVideo.membership_ids || []).length === 0 ? (
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Select membership plans above to set expiry dates
          </p>
        ) : (
          (currentVideo.membership_ids || []).map((id) => (
            <MembershipExpiryPicker
              key={id}
              membershipId={Number(id)}
              label={getMembershipLabel(id)}
              expiryMap={expiryMap}
              onSet={setMembershipExpiry}
              onClear={clearMembershipExpiry}
              isSubmitting={isSubmitting}
            />
          ))
        )}
      </div>

      {/* ── Attachment (Optional) ── */}
      <div className={styles.attachmentWrapper}>
        <div className={styles.attachmentHeader}>
          <span className={styles.attachmentLabel}>Attach File</span>
          <span className={styles.attachmentOptional}>Optional</span>
        </div>

        {!currentVideo?.attachment_file &&
        !currentVideo?.attachment_file_url ? (
          <label className={styles.attachmentBox}>
            <div className={styles.attachmentPlaceholder}>
              <span>📎</span>
              <span>Choose file (PDF, Doc, etc.)</span>
            </div>
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleAttachmentChange}
              disabled={isSubmitting}
            />
          </label>
        ) : (
          <div className={styles.attachmentBox}>
            <div className={styles.attachmentFile}>
              <span>📎</span>
              <span>
                {currentVideo.attachment_file
                  ? currentVideo.attachment_file.name
                  : currentVideo.attachment_file_url}
              </span>
            </div>
            <button
              type="button"
              className={styles.attachmentRemove}
              onClick={removeAttachment}
              disabled={isSubmitting}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* ── Add / Update Button ── */}
      <button
        className={styles.submitButton}
        onClick={handleAddOrUpdateVideo}
        disabled={isSubmitting}
      >
        <span>{editVideoIndex === -1 ? "Add Video" : "Update Video"}</span>
        <span className={styles.submitIcon}>
          {editVideoIndex === -1 ? "+" : "✓"}
        </span>
      </button>
    </>
  );
};

export default VideoForm;
